import { useEffect, useRef } from 'react'
import { useOktaAuth } from '@okta/okta-react'
import { showWarningToast } from 'ui/components/Toast/toast'
import { useUnsavedGuard } from 'contexts/UnsavedGuard'

function AutoLogoffManager() {
  const { oktaAuth } = useOktaAuth()
  const { suppressGuard } = useUnsavedGuard()
  const warningTimeoutRef = useRef(null)
  const logoutTimeoutRef = useRef(null)
  const LAST_ACTIVITY_KEY = 'AGRO_GSAT_LAST_ACTIVITY_TS'
  const LOGOUT_KEY = 'AGRO_GSAT_BROADCAST_LOGOUT'

  useEffect(() => {
    const INACTIVITY_MS = 20 * 60 * 1000 // 20 minutes
    const WARNING_LEAD_MS = 60 * 1000 // 1 minute before logout

    const clearTimers = () => {
      if (warningTimeoutRef.current) clearTimeout(warningTimeoutRef.current)
      if (logoutTimeoutRef.current) clearTimeout(logoutTimeoutRef.current)
    }

    const performLogout = async () => {
      try {
        clearTimers()
        // Suppress unsaved changes prompts during forced logout
        suppressGuard()

        // Broadcast logout to other tabs first
        try {
          localStorage.setItem(LOGOUT_KEY, String(Date.now()))
        } catch (e) {
          // ignore storage errors
        }
        // Clear LAST_ACTIVITY_KEY to prevent immediate reschedule after logout
        try {
          localStorage.removeItem(LAST_ACTIVITY_KEY)
        } catch (e) {
          // ignore storage errors
        }

        await oktaAuth.signOut({ postLogoutRedirectUri: `${window.location.origin}` })
      } catch (e) {
        window.location.href = '/login'
      }
    }

    const scheduleTimers = () => {
      clearTimers()

      const last = Number(localStorage.getItem(LAST_ACTIVITY_KEY) || '0')
      const now = Date.now()
      const elapsed = last ? now - last : INACTIVITY_MS
      const remaining = Math.max(INACTIVITY_MS - elapsed, 0)

      if (remaining <= 0) {
        // Session already expired while tab was closed
        performLogout()
        return
      }

      const warnIn = Math.max(remaining - WARNING_LEAD_MS, 0)
      warningTimeoutRef.current = setTimeout(() => {
        showWarningToast('You will be logged off in 1 minute due to inactivity.', 60000)
      }, warnIn)

      logoutTimeoutRef.current = setTimeout(() => {
        performLogout()
      }, remaining)
    }

    const handleActivity = () => {
      // Broadcast activity to all tabs
      try {
        localStorage.setItem(LAST_ACTIVITY_KEY, String(Date.now()))
      } catch (e) {
        // ignore storage errors
      }
      scheduleTimers()
    }

    // Initialize last activity at mount if absent, then schedule based on remaining time
    try {
      if (!localStorage.getItem(LAST_ACTIVITY_KEY)) {
        localStorage.setItem(LAST_ACTIVITY_KEY, String(Date.now()))
      }
    } catch (e) {
      // ignore storage errors
    }

    scheduleTimers()
    const events = ['mousemove', 'keydown', 'click', 'scroll', 'touchstart']
    events.forEach((evt) => window.addEventListener(evt, handleActivity, { passive: true }))

    // Listen for cross-tab activity
    const onStorage = (e) => {
      if (e.key === LAST_ACTIVITY_KEY) {
        // Another tab reported activity; reschedule
        scheduleTimers()
      }
      if (e.key === LOGOUT_KEY) {
        // Another tab initiated logout; perform local cleanup and redirect
        performLogout()
      }
    }
    window.addEventListener('storage', onStorage)

    return () => {
      clearTimers()
      events.forEach((evt) => window.removeEventListener(evt, handleActivity))
      window.removeEventListener('storage', onStorage)
    }
  }, [oktaAuth, suppressGuard])

  return null
}

export default AutoLogoffManager
